import { Capacitor } from "@capacitor/core";

/**
 * Handle for managing keyboard event listeners.
 * Provides a remove() method to unsubscribe from events.
 */
export interface KeyboardListenerHandle {
  remove: () => Promise<void>;
}

/**
 * Keyboard event types that can be listened to.
 */
export type KeyboardEventType =
  | "keyboardWillShow"
  | "keyboardDidShow"
  | "keyboardWillHide"
  | "keyboardDidHide";

/**
 * Shows the keyboard programmatically.
 * On native platforms, displays the soft keyboard.
 * On web, this is a no-op.
 */
export async function showKeyboard(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  const { Keyboard } = await import("@capacitor/keyboard");
  await Keyboard.show();
}

/**
 * Hides the keyboard programmatically.
 * On native platforms, dismisses the soft keyboard.
 * On web, this is a no-op.
 */
export async function hideKeyboard(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  const { Keyboard } = await import("@capacitor/keyboard");
  await Keyboard.hide();
}

/**
 * Adds a listener for keyboard events.
 * On native platforms, listens for keyboard show/hide events.
 * On web, returns a no-op listener handle.
 *
 * @param eventType - The event type to listen for
 * @param callback - The callback to invoke when the event fires
 * @returns A handle that can be used to remove the listener
 */
export async function addKeyboardListener(
  eventType: KeyboardEventType,
  callback: () => void
): Promise<KeyboardListenerHandle> {
  if (!Capacitor.isNativePlatform()) {
    return {
      remove: async () => {
        // No-op for web platform
      },
    };
  }

  const { Keyboard } = await import("@capacitor/keyboard");

  // TypeScript requires specific literal types for addListener
  switch (eventType) {
    case "keyboardWillShow": {
      return await Keyboard.addListener("keyboardWillShow", callback);
    }
    case "keyboardDidShow": {
      return await Keyboard.addListener("keyboardDidShow", callback);
    }
    case "keyboardWillHide": {
      return await Keyboard.addListener("keyboardWillHide", callback);
    }
    case "keyboardDidHide": {
      return await Keyboard.addListener("keyboardDidHide", callback);
    }
    default: {
      // Exhaustiveness check - this should never happen at runtime
      // but ensures TypeScript catches any missing cases
      throw new Error("Unknown keyboard event type");
    }
  }
}
